/**
 * /admin/venues — every venue on the platform.
 *
 * Platform-scoped, so there is no :venueSlug and no by-slug resolution step:
 * one GET, optionally narrowed by billing_status.
 *
 * PENDING VENUES ARE SPLIT OUT rather than left to sort themselves into the
 * main list. A venue sitting in `pending` is a queue item waiting on a human,
 * and the list is ordered by created_at DESC — an application filed a month ago
 * would sink below every venue created since and be invisible exactly when it
 * matters. The split section is subordinate to that, not decoration.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api.js';
import { formatStampDate } from '../lib/format.js';
import BillingBadge, { BILLING_STATUSES, STATUS_LABELS } from '../components/BillingBadge.jsx';

/**
 * "The Hop!" -> "the-hop". A copy of functions/lib/venues.js's slugify, because
 * the backend one cannot be imported into the bundle.
 *
 * It only has to AGREE with the server for the preview to be honest — the
 * server slugifies whatever is posted anyway, and collisions are still resolved
 * there. A drift here shows the admin a slug one character off, not a broken
 * venue.
 */
function slugify(name) {
  const slug = String(name ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

  return slug;
}

function VenueTable({ venues }) {
  return (
    <div className="hd-table-wrap">
      <table className="hd-table hd-admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Owner</th>
            <th>Status</th>
            <th className="hd-table-num">Players</th>
            <th className="hd-table-num">Tournaments</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {venues.map((v) => (
            <tr key={v.id}>
              <td><Link to={`/admin/venues/${v.id}`}>{v.name}</Link></td>
              <td className="hd-muted">{v.slug}</td>
              <td>{v.ownerName ?? v.ownerEmail ?? '—'}</td>
              <td><BillingBadge status={v.billingStatus} /></td>
              <td className="hd-table-num">{v.playerCount}</td>
              <td className="hd-table-num">{v.tournamentCount}</td>
              <td className="hd-meta">{formatStampDate(v.createdAt) ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const EMPTY_FORM = { venueName: '', slug: '', ownerEmail: '', ownerName: '' };

function AddVenueForm({ onCreated, onCancel }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The slug tracks the name until the admin touches it, then stops. Without
  // the flag, typing a slug and going back to fix a typo in the name silently
  // throws the slug away.
  const onName = (venueName) => {
    setForm((f) => ({
      ...f,
      venueName,
      slug: slugEdited ? f.slug : slugify(venueName),
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    // An empty slug is omitted rather than sent: the endpoint allocates one and
    // dedupes it, which is better than posting our unchecked guess.
    const { data, error: err } = await apiPost('/api/admin/venues', {
      venueName:  form.venueName.trim(),
      ownerEmail: form.ownerEmail.trim(),
      ownerName:  form.ownerName.trim(),
      ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
    });

    setBusy(false);

    if (err) { setError(err); return; }

    setForm(EMPTY_FORM);
    setSlugEdited(false);
    onCreated(data);
  };

  return (
    <form className="hd-card hd-admin-form hd-stack" onSubmit={onSubmit}>
      <h2 className="hd-section-title">Add venue</h2>

      <div className="hd-field">
        <label className="hd-label" htmlFor="venue-name">Venue name</label>
        <input
          id="venue-name"
          className="hd-input"
          value={form.venueName}
          onChange={(e) => onName(e.target.value)}
          required
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="venue-slug">Slug</label>
        <input
          id="venue-slug"
          className="hd-input"
          value={form.slug}
          onChange={(e) => { setSlugEdited(true); setForm((f) => ({ ...f, slug: e.target.value })); }}
          placeholder="the-hop"
        />
        <span className="hd-hint">
          The venue's public address: /venues/{form.slug || 'the-hop'}
        </span>
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="owner-email">Owner email</label>
        <input
          id="owner-email"
          className="hd-input"
          type="email"
          value={form.ownerEmail}
          onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
          required
        />
      </div>

      <div className="hd-field">
        <label className="hd-label" htmlFor="owner-name">Owner name</label>
        <input
          id="owner-name"
          className="hd-input"
          value={form.ownerName}
          onChange={(e) => setForm((f) => ({ ...f, ownerName: e.target.value }))}
        />
      </div>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create venue'}
        </button>
        <button className="hd-btn hd-btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function PlatformVenues() {
  const [status, setStatus] = useState('');
  const [state, setState] = useState({ status: 'loading' });
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    const { data, error } = await apiGet(`/api/admin/venues${query}`);

    if (error) return { status: 'error', error };
    return { status: 'ready', venues: data?.venues ?? [] };
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    load().then((next) => { if (!cancelled) setState(next); });

    return () => { cancelled = true; };
  }, [load]);

  const onCreated = async (data) => {
    setAdding(false);

    // The temporary password is returned once and never again, so it goes in
    // the notice verbatim — the admin who created the venue is not the person
    // who owns it, and has to pass it on.
    const credentials = data?.credentials;
    setNotice(
      credentials
        ? `${data.venue.name} created. Temporary password for ${credentials.email}: ${credentials.tempPassword}`
        : `${data?.venue?.name ?? 'Venue'} created.`
    );

    setState(await load());
  };

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Venues</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  const { venues } = state;

  // Only when the filter is not already narrowing the list to one status —
  // a "Pending approval" heading over a list the user explicitly filtered to
  // pending is a second copy of the same thing.
  const split = status === '';
  const pending = split ? venues.filter((v) => v.billingStatus === 'pending') : [];
  const rest    = split ? venues.filter((v) => v.billingStatus !== 'pending') : venues;

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <div>
          <h1 className="hd-page-title">Venues</h1>
          <p className="hd-meta">
            {venues.length} {venues.length === 1 ? 'venue' : 'venues'}
            {status ? ` with status ${STATUS_LABELS[status] ?? status}` : ' on the platform'}
          </p>
        </div>

        <div className="hd-row">
          <select
            className="hd-select hd-admin-filter"
            aria-label="Filter by status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All statuses</option>
            {BILLING_STATUSES.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>

          <button
            className="hd-btn hd-btn-primary"
            type="button"
            onClick={() => { setAdding((open) => !open); setNotice(null); }}
          >
            Add venue
          </button>
        </div>
      </div>

      {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}

      {adding && <AddVenueForm onCreated={onCreated} onCancel={() => setAdding(false)} />}

      {venues.length === 0 && (
        <div className="hd-empty">
          {status
            ? `No venues with status ${STATUS_LABELS[status] ?? status}.`
            : 'No venues on the platform yet'}
        </div>
      )}

      {pending.length > 0 && (
        <section className="hd-admin-pending">
          <h2 className="hd-section-title">Pending approval</h2>
          <VenueTable venues={pending} />
        </section>
      )}

      {rest.length > 0 && (
        <section>
          {pending.length > 0 && <h2 className="hd-section-title">All venues</h2>}
          <VenueTable venues={rest} />
        </section>
      )}
    </div>
  );
}
